import { faFileCircleXmark } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useKitsJobTimeoutHandler } from '@hooks/kits/useKitsJobTimeoutHandler';
import { Kits } from '@pages/ManageProductPage/KitsPage/kits';
import { WizardErrorPage } from '@partful/features/instructions/ui/WizardErrorPage';
import { isJobInProgress } from '@partful/pages/product-library/ui/product-list/jobFiltering';
import { useInvalidateQueries } from '@partful/shared/lib/hooks/useInvalidateQueries';
import { useProductInventoryQuery } from '@partful/shared/lib/hooks/useProductInventoryQuery';
import { useQueryParams } from '@partful/shared/lib/hooks/useQueryParams';
import { Button } from '@partful/shared/ui/Button';
import { WizardPageContent, WizardPageLayout } from '@partful/shared/ui/layouts/WizardPageLayout';
import { LoadingMessage } from '@partful/shared/ui/LoadingMessage/LoadingMessage';
import { useConfiguration } from '@providers/Configuration';
import { KitsJob } from '@providers/job/types';
import { useTenant } from '@providers/Tenant';
import { productInventoryEndpoints } from '@services/productInventory';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorList } from './components/ErrorList';

const REFETCH_JOB_INTERVAL = 10000;

export function KitsFeedback() {
  const { accountId } = useTenant();

  const { t } = useTranslation('createProduct');

  const { name: productName } = useConfiguration();
  const { configurationPID } = useQueryParams('configurationPID');

  const [ignoreFailedJob, setIgnoreFailedJob] = useState<string | undefined>();

  const { invalidateContentProcessingJobsQueries, invalidateConfigurationsQuery } = useInvalidateQueries();

  const { data: jobs, isLoading: isDeterminingInitialJobStatus } = useProductInventoryQuery<KitsJob[]>(
    productInventoryEndpoints.contentProcessing.root,
    {
      account: accountId,
      jobType: 'Kits',
      configurationPID,
    }
  );

  // Source of truth for job status so if page is refreshed it'll show correct status
  const kitsJobData = jobs?.[0];

  // if there's an existing in progress job then start polling this job - this is a local variable derived from the source of truth
  const inProgressKitsRunID = kitsJobData && isJobInProgress(kitsJobData) ? kitsJobData?.runID : null;
  const { data: inProgressKitsJobData } = useProductInventoryQuery<KitsJob>(
    `${productInventoryEndpoints.contentProcessing.kits}/${inProgressKitsRunID}`,
    { account: accountId },
    {
      enabled: Boolean(inProgressKitsRunID),
      refetchInterval: REFETCH_JOB_INTERVAL,
      notifyOnChangeProps: 'all',
    }
  );

  useEffect(() => {
    /* istanbul ignore next - this will be better tested as an e2e test */
    if (['FAILED', 'COMPLETED'].includes(inProgressKitsJobData?.status ?? '')) {
      // Invalidate queries first so the page doesn't show loading screen and shows correct status
      invalidateConfigurationsQuery().then(() => {
        invalidateContentProcessingJobsQueries();
      });
    }
  }, [inProgressKitsJobData, invalidateContentProcessingJobsQueries, invalidateConfigurationsQuery]);

  const retryUpload = () => {
    setIgnoreFailedJob(kitsJobData?.runID);
  };

  const isInProgress =
    (kitsJobData && isJobInProgress(kitsJobData)) ||
    /* istanbul ignore next */ (inProgressKitsJobData && isJobInProgress(inProgressKitsJobData));

  // If the job is in progress then we need to check if it has timed out
  useKitsJobTimeoutHandler(kitsJobData, accountId);

  if (isDeterminingInitialJobStatus) {
    return null;
  }

  if (isInProgress) {
    // clear cached data
    return (
      <WizardPageLayout>
        <WizardPageContent>
          <LoadingMessage title={t('kits.form.processing.status')} message={t('freeToLeaveThisPage')} />
        </WizardPageContent>
      </WizardPageLayout>
    );
  }

  if (kitsJobData?.status === 'FAILED' && ignoreFailedJob !== kitsJobData?.runID) {
    return (
      <WizardErrorPage
        errorIcon={<FontAwesomeIcon icon={faFileCircleXmark} />}
        errorTitle={t('kits.form.failure.error', { productName })}
        errorMessage={<ErrorList runID={kitsJobData.runID} kitsJobData={kitsJobData} />}
        errorActions={<Button onClick={() => retryUpload()}>{t('kits.form.failure.uploadNewFile')}</Button>}
      />
    );
  }

  return <Kits />;
}
